import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "@tanstack/react-router";
import LayoutContentArea from "@/components/layout/LayoutContentArea";
import { FiClock } from "react-icons/fi";
import {
  DiscrepanciesTab,
  DiscrepanciesTabs,
} from "@/components/discrepancies/DiscrepancesPageTabs";
import { DiscrepanciesTab as DiscrepanciesTabPanel } from "@/components/discrepancies/data-discrepancies/DiscrepanciesTab";
import { SSIDSyncTab } from "@/components/discrepancies/ssid-discrepancies/SSIDSyncTab";
import { useDiscrepanciesPage } from "@/components/discrepancies/data-discrepancies/hooks/useDiscrepanciesPage";
import { MIDsTab } from "@/components/discrepancies/mid-discrepancies/MidsTab";

const VALID_TABS: DiscrepanciesTab[] = ["discrepancies", "ssid-sync", "mids"];

function hashToTab(hash: string): DiscrepanciesTab {
  const id = hash.replace("#", "") as DiscrepanciesTab;
  return VALID_TABS.includes(id) ? id : "discrepancies";
}

function DiscrepanciesPageHeading({ lastRunTime }: { lastRunTime?: string }) {
  const formatLastRunTime = (endTime: string | undefined) => {
    if (!endTime) return null;
    const date = new Date(endTime);
    if (isNaN(date.getTime())) return null;
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  return (
    <div className="d-flex align-items-center justify-content-between w-100">
      <h1 className="fs-4 mb-0">Discrepancies</h1>
      {lastRunTime && (
        <div className="d-flex align-items-center gap-2">
          <span style={{ color: "#334155", fontSize: "0.95rem" }}>
            Last discrepancy check:
          </span>
          <div
            className="d-flex align-items-center gap-2"
            style={{
              backgroundColor: "#F1F5F9",
              border: "1px solid #E2E8F0",
              borderRadius: "20px",
              padding: "8px 16px",
            }}
          >
            <FiClock size={16} color="#64748B" />
            <span
              style={{ fontWeight: 600, color: "#1E3A5F", fontSize: "0.95rem" }}
            >
              {formatLastRunTime(lastRunTime)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default function DiscrepanciesPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const activeTab = hashToTab(location.hash);
  const handleTabChange = (tab: DiscrepanciesTab) => navigate({ hash: tab });

  const { anomalyRun } = useDiscrepanciesPage();

  const topRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let el: HTMLElement | null = topRef.current;
    while (el) {
      const overflowY = window.getComputedStyle(el).overflowY;
      if (overflowY === "auto" || overflowY === "scroll") {
        el.scrollTop = 0;
        return;
      }
      el = el.parentElement;
    }
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, []);

  return (
    <LayoutContentArea
      headingText={
        <DiscrepanciesPageHeading lastRunTime={anomalyRun?.EndTime} />
      }
    >
      <div ref={topRef} />
      <DiscrepanciesTabs activeTab={activeTab} onTabChange={handleTabChange} />

      {activeTab === "discrepancies" && (
        <div
          role="tabpanel"
          id="tabpanel-discrepancies"
          aria-labelledby="tab-discrepancies"
        >
          <DiscrepanciesTabPanel />
        </div>
      )}

      {activeTab === "ssid-sync" && (
        <div
          role="tabpanel"
          id="tabpanel-ssid-sync"
          aria-labelledby="tab-ssid-sync"
        >
          <SSIDSyncTab />
        </div>
      )}

      {activeTab === "mids" && (
        <div role="tabpanel" id="tabpanel-mids" aria-labelledby="tab-mids">
          <MIDsTab />
        </div>
      )}
    </LayoutContentArea>
  );
}
